import { fabric } from 'fabric';

import '../content-studio/components/design-editor/fabric-setup';
import { loadFontsFor } from '../content-studio/components/design-editor/fonts';
import { fitTextWidth, loadImageElement, makeLine, makeRect, makeText, tag } from '../content-studio/components/design-editor/objects';
import { renderDesignScene } from '../content-studio/canvas/design-render';
import { anchorOnBox } from '../content-studio/components/design-editor/linked-graphics';
import { Graphic, graphicsOf } from './graphics';
import { StudioShot } from './studio-contract';

/**
 * A photo's words and graphics — its title, callouts, tags — as layers over the picture.
 *
 * The image model is never asked to paint text (it misspells, and the words
 * could not be changed afterwards). Instead the words are laid out here, as the
 * design editor's own objects, and flattened with the editor's own renderer:
 * the photo the vendor downloads has them, and Design opens with every word
 * and label still editable.
 */

const SERIF = 'Cormorant Garamond';
const SANS = 'Inter';
const MONO = 'IBM Plex Mono';
const WHITE = '#FFFFFF';

/** Whether a shot has words to draw on its photo. Films carry theirs in the edit instead. */
export function hasOverlay(shot: StudioShot): boolean {
    return shot.kind === 'image' && (!!shot.text?.content?.trim() || graphicsOf(shot).some((g) => g.type !== 'text' || !!g.text.trim()));
}

/** The family a text part is drawn in: its own face, else its serif/sans/mono. */
function familyOf(g: Extract<Graphic, { type: 'text' }>): string {
    // Archivo Black is the editor's; the design editor draws it as Archivo at its heaviest.
    if (g.family === 'Archivo Black') return 'Archivo';
    return g.family || FONT[g.font || 'sans'];
}

/** What the drawn words were made from — when it changes, they are drawn again. */
export function overlayKey(shot: StudioShot): string {
    return JSON.stringify({ t: shot.text?.content?.trim() ? shot.text : null, g: graphicsOf(shot) });
}

function shadow(unit: number): fabric.Shadow {
    return new fabric.Shadow({ color: 'rgba(0,0,0,0.35)', blur: Math.round(unit * 0.02), offsetX: 0, offsetY: 1 });
}

/** The title (or line, or tag) where the shot says it goes. */
function titleObjects(shot: StudioShot, w: number, h: number): fabric.Object[] {
    const text = shot.text;
    if (!text?.content?.trim()) return [];
    const unit = Math.min(w, h);
    const font = text.font === 'sans' ? SANS : SERIF;
    const pos = text.position || (text.role === 'label' ? 'bottom-right' : text.role === 'title' ? 'top' : 'bottom');
    const align = pos === 'bottom-right' ? 'right' : pos === 'bottom-left' ? 'left' : 'center';
    const margin = unit * 0.07;
    const box = { x: margin, w: w - margin * 2 };

    const lines = text.content.split('\n').map((l) => l.trim()).filter(Boolean);
    const main = lines.length > 1 && text.role === 'title' ? lines.slice(1).join('\n') : lines.join('\n');
    const len = Math.max(...main.split('\n').map((l) => l.length));
    const size = text.role === 'title'
        ? unit * (len <= 12 ? 0.12 : len <= 20 ? 0.095 : len <= 32 ? 0.07 : 0.055)
        : text.role === 'label' ? unit * 0.034 : unit * 0.045;

    const objs: fabric.Textbox[] = [];
    // A title written on two lines reads as a kicker over a headline
    // ("Genimo × Vintage / The Art Rug"): the first line small, the rest large.
    if (lines.length > 1 && text.role === 'title') {
        objs.push(makeText(lines[0], { x: box.x, y: 0, w: box.w, size: size * 0.42, font, weight: font === SERIF ? 500 : 400, color: WHITE, align, lineHeight: 1.1, name: 'Kicker', fontRole: 'body' }));
    }
    objs.push(makeText(main, {
        x: box.x, y: 0, w: box.w, size, font, weight: font === SERIF ? 500 : 400, color: WHITE, align,
        lineHeight: text.role === 'title' ? 1.0 : 1.2,
        upper: text.role === 'label', spacing: text.role === 'label' ? 120 : text.role === 'title' ? -10 : 0,
        name: text.role === 'title' ? 'Title' : 'Text', fontRole: 'heading',
    }));

    const gap = size * 0.18;
    const total = objs.reduce((sum, o) => sum + (o.height || 0), 0) + gap * (objs.length - 1);
    let y = pos === 'center' ? (h - total) / 2 : pos === 'top' ? margin : h - margin - total;
    for (const o of objs) {
        o.set({ top: y });
        o.shadow = shadow(unit);
        o.setCoords();
        y += (o.height || 0) + gap;
    }
    return objs;
}

const FONT = { serif: SERIF, sans: SANS, mono: MONO } as const;
const ROLE = { text: 'words', rect: 'box', line: 'line', dot: 'dot', logo: 'logo' } as const;

/** Name and links on a part, so Design lists it by name and keeps its partners with it. */
function mark(o: fabric.Object, g: Graphic, extra: Record<string, unknown> = {}): fabric.Object {
    const name = g.group ? `${g.group} · ${ROLE[g.type]}` : g.type === 'text' ? g.text : ROLE[g.type];
    tag(o, { role: g.type === 'text' ? 'text' : g.type === 'line' ? 'divider' : 'shape', name });
    Object.assign(o as any, { csNameUser: true, csGfxId: g.id, ...extra });
    return o;
}

/**
 * The shot's graphics as separate layers — words, boxes, lines, dots — laid
 * out from their parts. Geometry that must agree is resolved here: a box is
 * fitted around the words it wraps, a line leaves the facing side of its box
 * and ends on its dot.
 */
function graphicObjects(list: Graphic[], w: number, h: number, logo?: HTMLImageElement | null): fabric.Object[] {
    const unit = Math.min(w, h);
    const px = (v: number | undefined, d: number) => (v ?? d) * (w / 1080);
    const byId = new Map<string, fabric.Object>();
    const rects: fabric.Object[] = [];
    const lines: fabric.Object[] = [];
    const dots: fabric.Object[] = [];
    const texts: fabric.Object[] = [];

    for (const g of list) {
        if (g.type !== 'text' || !g.text.trim()) continue;
        const size = (g.size ?? 0.02) * h;
        const font = familyOf(g);
        const tb = makeText(g.text, {
            x: 0, y: 0, w: w * 0.9, size, font, weight: g.family === 'Archivo Black' ? 800 : g.weight ?? 400, color: g.color || WHITE,
            align: g.align || 'left', upper: !!g.upper, italic: !!g.italic, spacing: g.spacing ?? 0, lineHeight: 1.1,
        });
        // Hollow letters: the outline in the text's colour, nothing inside.
        if (g.outline) tb.set({ fill: 'rgba(0,0,0,0)', stroke: g.color || WHITE, strokeWidth: Math.max(1, size * 0.025) } as any);
        fitTextWidth(tb);
        const tw = tb.width || 0;
        const th = tb.height || 0;
        const left = g.align === 'center' ? g.x * w - tw / 2 : g.align === 'right' ? g.x * w - tw : g.x * w;
        tb.set({ left: Math.max(0, Math.min(w - tw, left)), top: Math.max(0, Math.min(h - th, g.y * h - th / 2)) });
        tb.shadow = shadow(unit * 0.6);
        tb.setCoords();
        mark(tb, g, { csGfxUpper: g.upper ? true : undefined });
        if (g.id) byId.set(g.id, tb);
        texts.push(tb);
    }
    for (const g of list) {
        if (g.type !== 'rect') continue;
        const stroke = px(g.stroke, 1.5);
        let box: { x: number; y: number; w: number; h: number } | null = null;
        let pads: { padX: number; padY: number } | null = null;
        const words = g.wrap ? byId.get(g.wrap) : undefined;
        if (words) {
            const padY = (g.pad ?? 0.01) * h;
            const padX = padY * 1.5;
            const b = words.getBoundingRect(true, true);
            box = { x: b.left - padX, y: b.top - padY, w: b.width + padX * 2, h: b.height + padY * 2 };
            pads = { padX, padY };
        } else if (g.x !== undefined && g.y !== undefined && g.w !== undefined && g.h !== undefined) {
            box = { x: g.x * w, y: g.y * h, w: g.w * w, h: g.h * h };
        }
        if (!box) continue;
        // Texts sitting on the border cut it: the frame is drawn as its four
        // edges with an opening behind each of them ("UP TO" on the top line).
        const cuts = (g.breaks || []).map((id) => byId.get(id)).filter(Boolean).map((o) => o!.getBoundingRect(true, true));
        if (cuts.length && stroke > 0) {
            const gap = 0.012 * w;
            const x0 = box.x; const y0 = box.y; const x1 = box.x + box.w; const y1 = box.y + box.h;
            const edge = (ax: number, ay: number, bx: number, by: number): string => {
                const horizontal = Math.abs(ay - by) < 1e-6;
                const opens = cuts
                    .filter((c) => (horizontal ? c.top <= ay && c.top + c.height >= ay : c.left <= ax && c.left + c.width >= ax))
                    .map((c) => (horizontal ? [c.left - gap, c.left + c.width + gap] : [c.top - gap, c.top + c.height + gap]))
                    .sort((p, q) => p[0] - q[0]);
                let d = '';
                let at = horizontal ? ax : ay;
                const end = horizontal ? bx : by;
                for (const [a, b] of opens) {
                    if (a > at) d += horizontal ? `M ${at} ${ay} L ${a} ${ay} ` : `M ${ax} ${at} L ${ax} ${a} `;
                    at = Math.max(at, b);
                }
                if (at < end) d += horizontal ? `M ${at} ${ay} L ${end} ${ay} ` : `M ${ax} ${at} L ${ax} ${end} `;
                return d;
            };
            const d = edge(x0, y0, x1, y0) + edge(x0, y1, x1, y1) + edge(x0, y0, x0, y1) + edge(x1, y0, x1, y1);
            const frame = new fabric.Path(d.trim(), { fill: '', stroke: g.color || WHITE, strokeWidth: stroke, strokeLineCap: 'butt', objectCaching: false } as any);
            frame.shadow = shadow(unit * 0.3);
            mark(frame, g);
            if (g.id) byId.set(g.id, frame);
            rects.push(frame);
            continue;
        }
        const r = makeRect({
            x: box.x - stroke / 2, y: box.y - stroke / 2, w: box.w, h: box.h,
            fill: g.fill || 'rgba(0,0,0,0)', stroke: stroke > 0 ? (g.color || WHITE) : undefined, strokeWidth: stroke,
            rx: (g.radius ?? 0) * h,
        });
        r.shadow = shadow(unit * 0.3);
        mark(r, g, { csGfxWrap: words ? g.wrap : undefined, csGfxPadX: pads?.padX, csGfxPadY: pads?.padY });
        if (g.id) byId.set(g.id, r);
        rects.push(r);
    }
    for (const g of list) {
        if (g.type !== 'dot') continue;
        const radius = (g.r ?? 0.006) * unit;
        const dot = new fabric.Circle({
            left: g.x * w, top: g.y * h, radius, originX: 'center', originY: 'center',
            fill: g.ring ? 'rgba(0,0,0,0)' : (g.color || WHITE), stroke: g.ring ? (g.color || WHITE) : undefined, strokeWidth: g.ring ? Math.max(1.5, radius * 0.35) : 0,
        } as any);
        dot.shadow = shadow(unit * 0.3);
        mark(dot, g);
        if (g.id) byId.set(g.id, dot);
        dots.push(dot);
    }
    for (const g of list) {
        if (g.type !== 'line') continue;
        const target = g.to ? byId.get(g.to) : undefined;
        const end = target ? target.getCenterPoint() : g.x2 !== undefined && g.y2 !== undefined ? { x: g.x2 * w, y: g.y2 * h } : null;
        const source = g.from ? byId.get(g.from) : undefined;
        const start = source
            ? (end ? anchorOnBox(source.getBoundingRect(true, true), end) : null)
            : g.x1 !== undefined && g.y1 !== undefined ? { x: g.x1 * w, y: g.y1 * h } : null;
        if (!start || !end) continue;
        const l = makeLine(start.x, start.y, end.x, end.y, { color: g.color || WHITE, width: px(g.stroke, 1.5), dash: g.dash ? [6, 5] : undefined });
        l.shadow = shadow(unit * 0.3);
        mark(l, g, { csGfxFrom: source ? g.from : undefined, csGfxTo: target ? g.to : undefined });
        lines.push(l);
    }
    // The vendor's logo where the reference had one; nothing when they have none.
    const logos: fabric.Object[] = [];
    for (const g of list) {
        if (g.type !== 'logo' || !logo?.naturalWidth) continue;
        const lw = (g.w ?? 0.3) * w;
        const img = new fabric.Image(logo, { left: g.x * w, top: g.y * h, originX: 'center', originY: 'center' } as any);
        img.scaleToWidth(lw);
        mark(img, g);
        logos.push(img);
    }
    // Boxes under lines under dots under words.
    return [...rects, ...lines, ...dots, ...texts, ...logos];
}

/** The overlay as an editor scene: layers only, laid out at the photo's own size. */
export function buildOverlayScene(shot: StudioShot, w: number, h: number, logo?: HTMLImageElement | null): any {
    const canvas = new fabric.StaticCanvas(null as any, { width: w, height: h, enableRetinaScaling: false, renderOnAddRemove: false } as any);
    try {
        // When the graphics carry words, they carry the headline too, laid out
        // where the reference had it; the generic title would land on top of them.
        const parts = shot.kind === 'image' ? graphicsOf(shot) : [];
        if (!parts.some((g) => g.type === 'text' && g.text.trim())) titleObjects(shot, w, h).forEach((o) => canvas.add(o));
        if (shot.kind === 'image') graphicObjects(graphicsOf(shot), w, h, logo).forEach((o) => canvas.add(o));
        return { version: '5.3.0', authoredWidth: w, objects: (canvas.toObject() as any).objects || [] };
    } finally {
        canvas.dispose();
    }
}

/** Draw the shot's words on a photo; the PNG and the scene that Design reopens. */
export async function bakeOverlay(shot: StudioShot, photoUrl: string, brand?: { logoUrl?: string }): Promise<{ blob: Blob; scene: any }> {
    const img = await loadImageElement(photoUrl);
    const parts = graphicsOf(shot);
    const logo = brand?.logoUrl && parts.some((g) => g.type === 'logo') ? await loadImageElement(brand.logoUrl).catch(() => null) : null;
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    // Text is measured as it is laid out, so the faces must be there first —
    // a label boxed in the fallback font would not fit its own words.
    await loadFontsFor([
        { text: 'a', fontFamily: SERIF, fontWeight: 500 },
        { text: 'a', fontFamily: SANS, fontWeight: 500 },
        { text: 'a', fontFamily: SANS, fontWeight: 400 },
        { text: 'a', fontFamily: MONO, fontWeight: 400 },
        { text: 'a', fontFamily: SERIF, fontWeight: 400 },
        // The faces the reference's type was set in.
        ...parts.flatMap((g) => (g.type === 'text' ? [{ text: g.text || 'a', fontFamily: familyOf(g), fontWeight: g.family === 'Archivo Black' ? 800 : g.weight ?? 400, fontStyle: g.italic ? 'italic' : 'normal' }] : [])),
    ] as any).catch(() => undefined);
    const scene = buildOverlayScene(shot, w, h, logo);
    return renderDesignScene(photoUrl, scene, w, h);
}
